import * as React from 'react'
import styled from 'styled-components'
import * as phone from 'libphonenumber-js'

import * as S from '../../../styles'

import { PrefixSelect } from './PrefixSelect'
import { Country } from './types'

export type PhoneInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> & {
  className?: string
  countries: Country[]
  value: { number: string; country?: string }
  defaultCountry?: string
  status: 'default' | 'error' | 'success'
  filterPlaceholder?: string
  onChange?: (value: { number: string; country?: string }) => void
}

const parser = new phone.AsYouType('US')

// We don't control input with the NationalNumber value, because libphonenumber may return empty string.
// Like for example, in a french case, if user begins with 0 number at first, nationalNumber returns an empty string.
// Therefore, the input stays empty with no explanation for user.
// /!\ Therefore, do not forget to validate phone number value in your form, because PhoneInput doesn't format the value anymore!
function PhoneInputComponent({
  className,
  countries,
  value,
  onChange,
  disabled,
  defaultCountry,
  status,
  filterPlaceholder,
  ...inputProps
}: PhoneInputProps): React.ReactElement<PhoneInputProps> {
  parser.reset()
  parser.input(value.number)

  const number = parser.getNumber()
  const numberCountry = value.country || number?.country || defaultCountry
  const country = countries.find((country) => numberCountry === country.code) || countries[0]

  const nationalNumber = parser.getNationalNumber()
  const nationalNumberRef = React.useRef(nationalNumber)
  nationalNumberRef.current = nationalNumber

  function handleCountryChange(country: Country): void {
    // PrefixSelect is memo'ed to avoid rerendering all countries all the time
    // so handleCountryChange binding to onChange will happen only once on mount.
    // to have access to the current nationalNumber, we must use a ref
    const number = `+${country.prefix}${nationalNumberRef.current}`
    onChange &&
      onChange({
        number: number.replace(/\s/g, ''),
        country: country.code,
      })
  }

  function handleNationalChange(evt: React.ChangeEvent<HTMLInputElement>): void {
    const number = `+${country.prefix}${evt.target.value}`
    onChange &&
      onChange({
        number: number.replace(/\s/g, ''),
        country: country.code,
      })
  }

  return (
    <S.phone.Wrapper className={className} disabled={disabled} status={status}>
      <PrefixSelect
        disabled={disabled}
        countries={countries}
        selectedCountry={country}
        onChange={handleCountryChange}
        filterPlaceholder={filterPlaceholder}
      />
      <S.phone.Input
        {...inputProps}
        defaultValue={number?.format('NATIONAL', { nationalPrefix: true }).replace(/\s/g, '') || nationalNumber}
        type="tel"
        disabled={disabled}
        onChange={handleNationalChange}
      />
    </S.phone.Wrapper>
  )
}

/** will forward any other props to the underlying `<input>` */
export const PhoneInput = styled(PhoneInputComponent)<PhoneInputProps>`
  /* no border on select button, border done by <S.phone.Wrapper> */
  ${S.select.Button} {
    border: none;

    &:focus,
    &:hover,
    &:disabled {
      border: none;
    }
  }
`
PhoneInput.displayName = 'PhoneInput'
